import React, { useState } from "react";
import { AlertTriangle, Loader2, Moon, ShieldAlert, Sun, Trash2 } from "lucide-react";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";

import { PageHeader } from "@/components/common/PageHeader";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";
import { useTheme } from "@/contexts/ThemeContext";
import { useAuth } from "@/contexts/AuthContext";
import { adminService } from "@/services/admin.service";
import { getErrorMessage } from "@/services/api";

const CONFIRMATION_PHRASE = "LIMPAR TUDO";

export default function SettingsPage() {
    const { theme, setTheme } = useTheme();
    const { user } = useAuth();
    const isDark = theme === "dark";
    const isAdmin = user?.role === "Administrator";

    return (
        <div className="space-y-6">
            <PageHeader
                title="Configurações"
                description="Preferências visuais e ferramentas administrativas do sistema."
            />

            <div className="grid gap-6 lg:grid-cols-2">
                <Card className="rounded-xl border shadow-sm">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-semibold">Aparência</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="flex items-center justify-between">
                            <div className="flex items-start gap-3">
                                <div className="grid h-9 w-9 place-items-center rounded-lg bg-muted text-muted-foreground">
                                    {isDark ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
                                </div>
                                <div>
                                    <Label>Modo escuro</Label>
                                    <p className="text-xs text-muted-foreground">
                                        Alterne entre tema claro e escuro.
                                    </p>
                                </div>
                            </div>
                            <Switch
                                checked={isDark}
                                onCheckedChange={(v) => setTheme(v ? "dark" : "light")}
                                data-testid="settings-dark-toggle"
                            />
                        </div>
                    </CardContent>
                </Card>

                <Card className="rounded-xl border shadow-sm">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-semibold">Integrações futuras</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                        <p className="text-sm text-muted-foreground">
                            Este espaço está preparado para receber configurações dos próximos módulos:
                        </p>
                        <ul className="space-y-2 text-sm">
                            <li className="flex items-center gap-2">
                                <span className="h-1.5 w-1.5 rounded-full bg-secondary" />
                                Notificações por e-mail
                            </li>
                            <li className="flex items-center gap-2">
                                <span className="h-1.5 w-1.5 rounded-full bg-secondary" />
                                Orçamentos, contratos e faturamento
                            </li>
                            <li className="flex items-center gap-2">
                                <span className="h-1.5 w-1.5 rounded-full bg-secondary" />
                                IA de OCR real e manutenção preditiva
                            </li>
                            <li className="flex items-center gap-2">
                                <span className="h-1.5 w-1.5 rounded-full bg-secondary" />
                                Integrações SAP, TOTVS e Power BI
                            </li>
                        </ul>
                    </CardContent>
                </Card>
            </div>

            {isAdmin && <DangerZone />}
        </div>
    );
}

function DangerZone() {
    const [open, setOpen] = useState(false);

    return (
        <Card
            className="rounded-xl border-2 border-destructive/50 bg-destructive/5 shadow-sm"
            data-testid="danger-zone-card"
        >
            <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-sm font-semibold text-destructive">
                    <ShieldAlert className="h-4 w-4" />
                    Zona de Perigo
                </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
                <div className="rounded-lg border border-destructive/40 bg-background p-4">
                    <div className="mb-2 flex items-center gap-2">
                        <Trash2 className="h-4 w-4 text-destructive" />
                        <span className="font-medium">Limpar dados operacionais</span>
                    </div>
                    <p className="mb-3 text-xs text-muted-foreground">
                        Remove <b>permanentemente</b> todos os clientes, equipamentos, técnicos, ordens de serviço,
                        propostas, oficinas, fornecedores, contas financeiras e segmentos <b>desta empresa</b>.
                    </p>
                    <p className="mb-3 text-xs text-muted-foreground">
                        <b>Serão preservados:</b> usuários do sistema, dados da empresa e histórico de auditoria.
                    </p>
                    <p className="mb-3 text-xs font-medium text-destructive">
                        ⚠️ Esta ação é irreversível. Use somente quando quiser começar do zero.
                    </p>
                    <Dialog open={open} onOpenChange={setOpen}>
                        <DialogTrigger asChild>
                            <Button
                                variant="destructive"
                                data-testid="open-wipe-dialog"
                                className="gap-2"
                            >
                                <Trash2 className="h-4 w-4" />
                                Limpar todos os dados
                            </Button>
                        </DialogTrigger>
                        <WipeDialog onClose={() => setOpen(false)} />
                    </Dialog>
                </div>
            </CardContent>
        </Card>
    );
}

function WipeDialog({ onClose }) {
    const [password, setPassword] = useState("");
    const [confirmation, setConfirmation] = useState("");
    const [result, setResult] = useState(null);

    const wipe = useMutation({
        mutationFn: (payload) => adminService.wipeData(payload),
        onSuccess: (data) => {
            setResult(data);
            const total = Object.values(data.deleted || {}).reduce((s, n) => s + n, 0);
            toast.success(`Dados apagados: ${total} registro(s) removido(s).`);
        },
        onError: (e) => {
            toast.error(getErrorMessage(e, "Falha ao limpar dados."));
        },
    });

    const handleSubmit = (e) => {
        e.preventDefault();
        wipe.mutate({ password, confirmation });
    };

    const isValid = password.length > 0 && confirmation.trim().toUpperCase() === CONFIRMATION_PHRASE;

    if (result) {
        const totalDeleted = Object.values(result.deleted || {}).reduce((s, n) => s + n, 0);
        return (
            <DialogContent data-testid="wipe-result-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-emerald-700">
                        <Trash2 className="h-4 w-4" />
                        Limpeza concluída
                    </DialogTitle>
                    <DialogDescription>
                        {totalDeleted} registro(s) foram removidos com sucesso.
                    </DialogDescription>
                </DialogHeader>
                <div className="max-h-64 space-y-1 overflow-y-auto rounded-md border bg-muted/30 p-3 text-xs">
                    {Object.entries(result.deleted || {})
                        .filter(([, n]) => n > 0)
                        .map(([coll, n]) => (
                            <div key={coll} className="flex justify-between">
                                <span className="capitalize text-muted-foreground">{coll.replace(/_/g, " ")}</span>
                                <span className="font-mono font-medium">{n}</span>
                            </div>
                        ))}
                    {totalDeleted === 0 && (
                        <div className="text-center text-muted-foreground">
                            Nenhum dado precisou ser removido.
                        </div>
                    )}
                </div>
                <DialogFooter>
                    <Button
                        onClick={() => {
                            setResult(null);
                            setPassword("");
                            setConfirmation("");
                            onClose?.();
                            // Force reload so all queries refetch
                            window.location.reload();
                        }}
                        data-testid="wipe-done-button"
                    >
                        Concluir e recarregar
                    </Button>
                </DialogFooter>
            </DialogContent>
        );
    }

    return (
        <DialogContent data-testid="wipe-dialog">
            <form onSubmit={handleSubmit}>
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-destructive">
                        <AlertTriangle className="h-4 w-4" />
                        Confirmar limpeza de dados
                    </DialogTitle>
                    <DialogDescription className="space-y-2 pt-2">
                        <span className="block">
                            Esta ação removerá <b>permanentemente</b> todos os dados operacionais desta
                            empresa. Usuários, empresa e histórico de auditoria serão preservados.
                        </span>
                        <span className="block font-medium text-destructive">
                            Não é possível desfazer.
                        </span>
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-4">
                    <div className="space-y-1.5">
                        <Label htmlFor="wipe-password" className="text-xs">
                            Sua senha de administrador
                        </Label>
                        <Input
                            id="wipe-password"
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder="Digite sua senha atual"
                            autoComplete="current-password"
                            data-testid="wipe-password-input"
                        />
                    </div>

                    <div className="space-y-1.5">
                        <Label htmlFor="wipe-confirmation" className="text-xs">
                            Digite <span className="font-mono font-bold">{CONFIRMATION_PHRASE}</span> para confirmar
                        </Label>
                        <Input
                            id="wipe-confirmation"
                            value={confirmation}
                            onChange={(e) => setConfirmation(e.target.value)}
                            placeholder={CONFIRMATION_PHRASE}
                            autoComplete="off"
                            data-testid="wipe-confirmation-input"
                        />
                    </div>
                </div>

                <DialogFooter className="gap-2">
                    <Button
                        type="button"
                        variant="ghost"
                        onClick={onClose}
                        disabled={wipe.isPending}
                        data-testid="wipe-cancel-button"
                    >
                        Cancelar
                    </Button>
                    <Button
                        type="submit"
                        variant="destructive"
                        disabled={!isValid || wipe.isPending}
                        data-testid="wipe-confirm-button"
                    >
                        {wipe.isPending ? (
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : (
                            <Trash2 className="mr-2 h-4 w-4" />
                        )}
                        Sim, apagar tudo
                    </Button>
                </DialogFooter>
            </form>
        </DialogContent>
    );
}
